import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewContainerRef,
  TemplateRef,
} from '@angular/core';
import {
  AuditService,
  IngredientChoice,
  OrderableItem,
  OrderedChoice,
  OrderedItem,
  OrderedItemService,
  Picture,
  SizePrice,
  Upsell,
  DeserializationUtils,
  ItemWithIngredientPrices,
  StoreConfig,
  OrderedIngredientService,
  Utils,
  HypermediaService,
} from 'ngx-web-api';
import { combineLatest, Subject, Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, filter, map, tap, takeUntil, switchMap, catchError } from 'rxjs/operators';
import { Animations } from '../../animations';
import { EditingItemChoicesService } from '../../core/services/editing-item-choices.service';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { MenuWrapperService } from '../../core/services/menu-wrapper.service';
import { GTMService } from '../../core/services/gtm.service';
import { ViewportService } from '../../core/services/viewport.service';
import { Theme } from '../../domain/theme.model';
import { ViewPort } from '../../domain/view-port.model';
import { BuilderService } from '../builder.service';
import { StageIngredientAction } from '../domain/stage-ingredient-action.model';
import { ErrorsService } from '../../core/services/errors.service';
import { ParsedErrorResponse } from '../../domain/parsed-error-response.model';
import { ModalService } from '../../core/services/modal.service';
import { PrintIngredient } from '../domain/print-ingredient.model';
import { BreakpointService } from '../../core/services/breakpoint.service';
import { faCaretLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { faCaretCircleLeft, faCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { PreviousTabService } from 'app/core/services/previous-tab.service';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { Router } from '@angular/router';
import { OrderUpsellService } from 'app/core/services/order-upsell.service';
import { IngredientSelection } from 'app/domain/ingredient-selection';
import { ConfirmationModalContext } from 'app/domain/confirmation-modal-context';
import { NotificationModalType } from 'app/domain/notification-modal-type.enum';
import { EcommerceTriggeringArea } from '../../domain/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../../domain/ecommerce-triggering-point.enum';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { UiOrchestratorService } from 'app/core/services/ui-orchestrator.service';
import { OrderingUtilsService } from 'app/core/services/ordering-utils.service';
import { SpecialtyHalfs } from 'ngx-web-api/lib/models/menu/specialty-halfs.model';
import { HalfSelectionSideType } from 'ngx-web-api/lib/models/ordering/half-selection-side.enum';
import { HalfsOrderingService } from 'app/core/services/halfs-ordering.service';
import { HalfsPrintIngredientModel } from '../domain/halfs-print-ingredient.model';
import { ReloaderService } from 'app/core/services/reloader.service';
import { ScrollEventService } from 'app/shared/scroll-event.service';
import { FocusService } from 'app/focus.service';

@Component({
  selector: 'fts-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  animations: [Animations.fadeInOut, Animations.expanded],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input()
  imageSrc: string;
  @Input()
  orderableItem: OrderableItem;
  @Input()
  orderedItem: OrderedItem;
  @Input()
  upsellItems: Upsell[] = [];
  @Input()
  theme: Theme;
  @Input()
  isInCreationMode = false;
  @Input()
  storeConfig: StoreConfig;
  @Input()
  inItemEditorModal = false;

  @Output()
  triggerUpsellsOnSizeChange: EventEmitter<{ orderableItem: OrderableItem; orderedItem: OrderedItem }> = new EventEmitter();

  @ViewChild('panelsWrapper', { static: true })
  private panelsWrapper: ElementRef;
  @ViewChild('editItemContent', { static: false })
  private editItemContent: ElementRef;
  public viewPort$: Observable<ViewPort>;

  @ViewChild('outlet', { read: ViewContainerRef })
  private outletRef: ViewContainerRef;
  @ViewChild('content')
  private contentRef: TemplateRef<any>;
  @ViewChild('editItemControls', { static: false })
  editItemControlsRef: ElementRef;

  faCaretLeft = faCaretLeft;
  faCaretCircleLeft = faCaretCircleLeft;
  faCheck = faCheck;
  faCircleXmark = faCircleXmark;
  isItemValid: boolean;
  hideUpsellItems: boolean;
  orderedDescription: string;
  showFullDescription = false;
  price = 0;
  canvasNotSupported: boolean;
  calories: string;
  initialOrderedItem: OrderedItem;
  orderIsUpdated = false;
  loadingSubscription: Subscription;
  shouldNotFetchPrice = true;
  menuRouteLink: string[];
  previousCategory: string;
  builderImageLoading: boolean;
  itemPicture?: Picture;
  destroy$: Subject<void> = new Subject<void>();
  private visibleChoice?: IngredientChoice;
  errors: ParsedErrorResponse;
  private defaultIngredients: PrintIngredient[] = [];
  printableIngredients: PrintIngredient[] = [];
  faRemove = faTimes;
  scrollableElement: any;
  ingredientChoicesAsSteps: boolean;
  choicesWithIngPrices: string[] = [];
  previousSelectedSize: string;
  ingredientSelectionsAddedToItem: IngredientSelection[] = [];
  defaultIngredientSelections: IngredientSelection[] = [];
  cachedOrderedItem: OrderedItem;
  previousFragment: string;
  imageLoaded: boolean;

  halfs: SpecialtyHalfs[];
  isHalfAndHalf: boolean;
  halfAndHalfLabel: string;
  blockIngredientsChangeOnHalfnHalfInit: boolean = false;
  isProcessingIngredientChange: boolean = false;

  activeHalfSide = HalfSelectionSideType.LEFT;
  halfSelectionSideType = HalfSelectionSideType;

  leftSelectedHalf: SpecialtyHalfs;
  leftPrintableIngredients: HalfsPrintIngredientModel[];
  rightSelectedHalf: SpecialtyHalfs;
  rightPrintableIngredients: HalfsPrintIngredientModel[];
  loadingSpecialtyHalfs = false;

  faAngleRight = faAngleRight;
  showSpecialtyHalfDetails = false;

  hiddenChoices = true;
  changedSizeInHalf = false;

  headerHeightOffset: number;
  previousQuantity: number;
  isSizeChanged: boolean = false;
  selectedSizePrice?: SizePrice;
  formattedNoticePeriod: string = '';

  constructor(
    private orderedItemService: OrderedItemService,
    private auditService: AuditService,
    private editingItemChoicesService: EditingItemChoicesService,
    private builderService: BuilderService,
    private menuWrapperService: MenuWrapperService,
    private itemEditorService: ItemEditorService,
    private cdRef: ChangeDetectorRef,
    private viewPortService: ViewportService,
    private gtmService: GTMService,
    private errorsService: ErrorsService,
    private modalService: ModalService,
    private translateService: TranslateService,
    public breakPointService: BreakpointService,
    private previousTabService: PreviousTabService,
    private router: Router,
    private orderUpsellService: OrderUpsellService,
    private orderedIngredientService: OrderedIngredientService,
    private liveAnnouncer: LiveAnnouncer,
    private uiService: UiOrchestratorService,
    private orderingUtilsService: OrderingUtilsService,
    private hypermedia: HypermediaService,
    private halfsOrderingService: HalfsOrderingService,
    private reloaderService: ReloaderService,
    private scrollService: ScrollEventService,
    private focusService: FocusService
  ) {
    this.viewPort$ = this.viewPortService.viewport$;
    this.imageLoaded = false;
  }

  ngOnInit() {
    this.editingItemChoicesService.visibleOrderedChoice$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((choice: OrderedChoice) => {
        if (!choice || !choice.ingredients) {
          return;
        }
        this.checkItemValidity();
      });
    combineLatest([this.itemEditorService.editingItemSelectedSizePrice$, this.itemEditorService.editingItemShouldShowFullCalorieInfo$])
      .pipe(
        tap(([sizePrice]) => {
          this.selectedSizePrice = sizePrice;
        }),
        map(([sizePrice, showWholeCalories]: [SizePrice, boolean]) =>
          this.menuWrapperService.getItemCalories(sizePrice, showWholeCalories)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((calories: string) => (this.calories = calories));
    combineLatest([
      this.itemEditorService.editingItemShouldShowFullCalorieInfo$,
      this.itemEditorService.sizeChanged$,
      this.itemEditorService.editingOrderableItem$,
    ])
      .pipe(
        filter(([_showWholeCalories, _size, orderableItem]) => !!orderableItem),
        map(([showWholeCalories, size, orderableItem]: [boolean, string, OrderableItem]) => [
          showWholeCalories,
          orderableItem.getSizePrice(size),
        ]),
        map(([showWholeCalories, sizePrice]: [boolean, SizePrice]) =>
          this.menuWrapperService.getItemCalories(sizePrice, showWholeCalories)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((calories: string) => (this.calories = calories));
    this.builderService.builderImageLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading: boolean) => ((this.builderImageLoading = loading), this.cdRef.detectChanges()));
    this.editingItemChoicesService.visibleChoice$
      .pipe(
        distinctUntilChanged(),
        filter(choice => !!choice),
        takeUntil(this.destroy$)
      )
      .subscribe((choice: IngredientChoice) => (this.visibleChoice = choice));

    this.updatePreviousCategory();

    this.halfsOrderingService.activeHalfSide$.pipe(takeUntil(this.destroy$)).subscribe(side => (this.activeHalfSide = side));
    this.halfsOrderingService.isHalfAndHalf$.pipe(takeUntil(this.destroy$)).subscribe(isHalfAndHalf => {
      this.isHalfAndHalf = isHalfAndHalf;
    });
    this.blockIngredientsChangeOnHalfnHalfInit = this.isHalfAndHalf;

    combineLatest([
      this.halfsOrderingService.activeHalfSide$,
      this.halfsOrderingService.leftHalfOrderedItem$,
      this.halfsOrderingService.rightHalfOrderedItem$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([activeHalf, leftHalf, rightHalf]) => {
        if ((activeHalf === HalfSelectionSideType.LEFT && !leftHalf) || (activeHalf === HalfSelectionSideType.RIGHT && !rightHalf)) {
          this.outletRef?.clear();
        }
      });

    this.halfsOrderingService.halfAndHalfLabel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(halfAndHalfLabel => (this.halfAndHalfLabel = halfAndHalfLabel));

    this.scrollService.resize$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isHalfAndHalf && this.editItemControlsRef && this.panelsWrapper && window.innerWidth < 768) {
        const editItemControlsHeight = this.editItemControlsRef.nativeElement.offsetHeight;
        this.panelsWrapper.nativeElement.style.paddingBottom = `${editItemControlsHeight}px`;
      }
      if (this.isHalfAndHalf) {
        this.showSpecialtyHalfDetails = false;
      }
      this.halfAndHalfHeightAdjustments();
    });
    this.formattedNoticePeriod = this.orderingUtilsService.formatNoticePeriod(this.selectedSizePrice?.noticePeriod);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.uiService.markedOrderInitializationDoneFromOrderTypeModal(false);
      this.halfAndHalfHeightAdjustments();
      this.focusService.applyFocus('editor-header');
    }, 500);
    this.scrollToEditItemContent();
    this.scrollableElement = this.panelsWrapper.nativeElement;
    this.cdRef.detectChanges();
    if (this.isHalfAndHalf) {
      this.outletRef?.clear();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.orderUpsellService.resetHalfAndHalfUpsells();
    this.halfsOrderingService.resetHalfs();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderableItem'] || changes['orderedItem']) {
      if (changes['orderableItem']) {
        this.orderableItem = DeserializationUtils.deserializeObj(changes['orderableItem'].currentValue, OrderableItem);
      }
      if (changes['orderedItem']) {
        this.orderedItem = DeserializationUtils.deserializeObj(changes['orderedItem'].currentValue, OrderedItem);
        this.fetchItems();
        if (this.orderedItem) {
          if (this.storeConfig.showSizeSpecificIngredients) {
            this.previousSelectedSize = this.orderedItem.size;
          }
          this.removeUnavailableChoices();
        }
        if (this.storeConfig.showSizeSpecificIngredients) {
          this.getDefaultIngredientSelections();
        }
        this.cdRef.detectChanges();
      }
      this.checkItemValidity();

      if (this.orderedItem && !this.shouldNotFetchPrice) {
        this.determinePrice();
      }
      if (!!this.orderableItem) {
        this.calcDefaultIngredients();
        this.calcPrintableIngredients();
      }
    }

    if (changes['orderableItem'] && changes['orderableItem'].firstChange && !!this.orderedItem) {
      this.setOrderedDescription();
    }

    if (changes['orderedItem'] && !!changes['orderedItem'].currentValue) {
      this.initialOrderedItem = this.orderedItem.copy();
      this.cachedOrderedItem = this.orderedItem.copy();
    }

    if (changes['orderableItem'] && this.orderableItem) {
      this.itemPicture = this.menuWrapperService.getPicture(this.orderableItem.pictures);
      this.calcIngredientChoicesType();
      this.choicesWithIngPrices = this.orderableItem.choices.filter(c => c.showIngredientPrices).map(c => c.name);
    }

    if (changes['theme'] && !!this.theme) {
      this.calcIngredientChoicesType();
    }
  }

  halfAndHalfHeightAdjustments() {
    if (this.isHalfAndHalf && this.editItemControlsRef && this.panelsWrapper) {
      const editItemControlsHeight = this.editItemControlsRef.nativeElement.offsetHeight;
      this.panelsWrapper.nativeElement.style.paddingBottom = window.innerWidth < 768 ? `${editItemControlsHeight}px` : '0';
    }
  }
  imgLoaded(e) {
    this.imageLoaded = true;
  }

  calcHalfIngredients() {
    if (!this.orderedItem) {
      return;
    }

    let tempPrintable: HalfsPrintIngredientModel[] = [];
    if (this.halfsOrderingService.leftHalfDefaults.getValue()) {
      tempPrintable = this.orderedItemService
        .getAllIngredients(this.orderedItem)
        .filter(ingredient => !ingredient.isRightHalf || (ingredient.isRightHalf && ingredient.secondHalfQualifiers?.length))
        .map(ingredient => {
          return {
            name: ingredient.ingredient,
            qualifiers: ingredient.qualifiers,
            isLeftHalf: ingredient.isLeftHalf,
            isRightHalf: ingredient.isRightHalf,
            secondHalfQualifiers: ingredient.secondHalfQualifiers,
          };
        });

      this.halfsOrderingService.leftHalfDefaults.getValue().forEach(ingredient => {
        const index = tempPrintable.findIndex(i => i.name === ingredient.ingredient);
        if (index > -1) {
          if (
            !tempPrintable[index].qualifiers?.length &&
            !(tempPrintable[index].isRightHalf && tempPrintable[index].secondHalfQualifiers?.length)
          ) {
            tempPrintable.splice(index, 1);
          }
        } else {
          tempPrintable.push({ name: ingredient.ingredient, isRemoved: true });
        }
      });

      this.leftPrintableIngredients = tempPrintable;
    }

    if (this.halfsOrderingService.rightHalfDefaults.getValue()) {
      tempPrintable = [];

      tempPrintable = this.orderedItemService
        .getAllIngredients(this.orderedItem)
        .filter(ingredient => !ingredient.isLeftHalf || (ingredient.isLeftHalf && ingredient.secondHalfQualifiers?.length))
        .map(ingredient => {
          return {
            name: ingredient.ingredient,
            qualifiers: ingredient.qualifiers,
            isLeftHalf: ingredient.isLeftHalf,
            isRightHalf: ingredient.isRightHalf,
            secondHalfQualifiers: ingredient.secondHalfQualifiers,
          };
        });

      this.halfsOrderingService.rightHalfDefaults.getValue().forEach(ingredient => {
        const index = tempPrintable.findIndex(i => i.name === ingredient.ingredient);
        if (index > -1) {
          if (
            !tempPrintable[index].qualifiers?.length &&
            !(tempPrintable[index].isLeftHalf && tempPrintable[index].secondHalfQualifiers?.length)
          ) {
            tempPrintable.splice(index, 1);
          }
        } else {
          tempPrintable.push({ name: ingredient.ingredient, isRemoved: true });
        }
      });

      this.rightPrintableIngredients = tempPrintable;
    }
  }

  handleIngredientNameClick(ingredient: string) {
    const visibleChoiceHasWarnings = this.orderedItem.choices.find(c => c.name === this.visibleChoice?.name).warnings.length === 0;
    if (visibleChoiceHasWarnings) {
      const choice = this.orderableItem.choices.find(c => !!c.ingredients.find(i => i.name === ingredient));

      if (!choice.isInstructions) {
        if (this.orderableItem.choiceGroups.length > 0) {
          const choiceGroup = this.orderableItem.choiceGroups.find(g => !!g.choices.find(c => c === choice.name));
          this.editingItemChoicesService.setVisibleChoiceGroup(choiceGroup);
        }

        this.editingItemChoicesService.setVisibleChoice(choice);
      }
    }
  }

  closeEditor() {
    if (this.inItemEditorModal) {
      if (!this.canLeaveEditor()) {
        this.modalService.canLeaveEditorModal();
      } else {
        this.modalService.closeAllModals();
      }
    } else {
      this.router.navigate(this.menuRouteLink, { fragment: this.previousFragment });
    }
  }

  public canLeaveEditor(): boolean {
    if (!this.initialOrderedItem) {
      return true;
    }

    if (this.isInCreationMode && !this.orderIsUpdated) {
      return false;
    }

    return this.orderedItemService.compareOrderedItems(this.initialOrderedItem, this.orderedItem);
  }

  onCloseItemEditorModal() {
    if (!this.canLeaveEditor()) {
      this.modalService.canLeaveEditorModal();
    } else {
      this.modalService.closeAllModals();
    }
  }

  updatePreviousCategory() {
    const previousTab = this.previousTabService.getPreviousTab();
    if (this.theme.hasSinglePageMenu && previousTab !== 'search') {
      this.menuRouteLink = ['/menu', 'single-page'];
      this.previousFragment = this.previousTabService.getPreviousFragment();
      this.previousCategory = this.previousTabService.getPreviousTabLabel();
      return;
    }
    if (!previousTab) {
      this.menuRouteLink = ['/menu'];
      this.previousCategory = 'Menu';
    } else {
      if (previousTab === 'RecentlyOrdered') {
        this.previousCategory = this.translateService.instant('component.item_editor.editor.recently_ordered');
      } else {
        this.previousCategory = previousTab;
      }
      this.menuRouteLink = ['/menu', previousTab];
    }
  }

  onAddOrUpdateItem() {
    this.orderUpsellService.setAddedOrderableItem(this.orderableItem);
    this.orderIsUpdated = true;
    this.initialOrderedItem = this.orderedItem.copy();
  }

  onIngredientChanged(selections?: IngredientSelection[]) {
    if (this.isProcessingIngredientChange) {
      return;
    }

    this.isProcessingIngredientChange = true;
    if ((this.changedSizeInHalf && this.isHalfAndHalf) || this.blockIngredientsChangeOnHalfnHalfInit) {
      this.blockIngredientsChangeOnHalfnHalfInit = false;
      this.isProcessingIngredientChange = false;
      return;
    }
    this.checkItemValidity();
    this.setOrderedDescription();
    const selectionDeterminesRecipe = !!selections?.find(s => s.ingredient.determinesRecipe);
    /** Do not call determinePrice() if the selection determines recipe
     * and show calories is enabled, because it will be triggered through
     * @see {ItemEditorService.subscribeToRecipeChanges}
     * which updates the orderableItem and triggers this component's
     *  @see {ngOnChanges}
     */
    if (!selections || !selectionDeterminesRecipe || (!this.storeConfig.showCalories && selectionDeterminesRecipe)) {
      this.determinePrice();
    } else if (selectionDeterminesRecipe && selections.some(s => !!s?.choice?.showIngredientImages)) {
      this.orderedItem = this.orderedItem.copy();
    }
    this.reCalculateDefaultQualifiers();
    this.calcPrintableIngredients();
    if (this.storeConfig.showSizeSpecificIngredients) {
      this.calcAddedSelections(selections);
    }

    this.isProcessingIngredientChange = false;
  }

  calcDefaultIngredients() {
    if (this.orderableItem && this.orderedItem) {
      this.defaultIngredients = [];
      this.orderableItem.choices.forEach(choices => {
        this.defaultIngredients.push(
          ...choices.ingredients
            .filter(ingredient => ingredient.isDefault && ingredient.sizePrices.find(sp => sp.size === this.orderedItem.size))
            .map(({ name, isDefault }) => {
              return {
                name,
                isDefault,
                isSelected: false,
                qualifiers: [],
                isLeftHalf: false,
                isRightHalf: false,
                secondHalfQualifiers: [],
              };
            })
        );
      });
      const tempDefaults = [];
      tempDefaults.push(
        ...this.orderedItemService
          .getAllIngredients(this.orderedItem)
          .filter(ingredient => ingredient.isDefault)
          .map(({ ingredient, isDefault, qualifiers, isLeftHalf, isRightHalf, secondHalfQualifiers }) => ({
            name: ingredient,
            isDefault,
            isSelected: true,
            qualifiers,
            isLeftHalf,
            isRightHalf,
            secondHalfQualifiers,
          }))
      );
      tempDefaults.forEach(element => {
        const pos = this.defaultIngredients.map(def => def.name).indexOf(element.name);
        if (pos !== -1) {
          this.defaultIngredients[pos] = element;
        }
      });
    }
  }

  reCalculateDefaultQualifiers() {
    const defaultIngredients: PrintIngredient[] = [];
    defaultIngredients.push(
      ...this.orderedItemService
        .getAllIngredients(this.orderedItem)
        .filter(ingredient => ingredient.isDefault)
        .map(({ ingredient, isDefault, qualifiers, isLeftHalf, isRightHalf, secondHalfQualifiers }) => ({
          name: ingredient,
          isDefault,
          isSelected: true,
          qualifiers,
          isLeftHalf,
          isRightHalf,
          secondHalfQualifiers,
        }))
    );
    this.defaultIngredients = this.defaultIngredients
      .filter(item => {
        return defaultIngredients.map(baseItem => baseItem.name).indexOf(item.name) === -1;
      })
      .map(item => ({
        name: item.name,
        isDefault: item.isDefault,
        isSelected: item.isSelected,
        qualifiers: [],
        isLeftHalf: false,
        isRightHalf: false,
        secondHalfQualifiers: [],
      }));
    this.defaultIngredients.push(...defaultIngredients);
  }

  calcPrintableIngredients() {
    if (this.orderedItem) {
      if (this.isHalfAndHalf) {
        this.calcHalfIngredients();
      } else {
        const visiblechoices = this.identifyVisibleChoices();
        const visibleIngredients = [].concat(...visiblechoices.map(choice => choice.ingredients)).map(ingredient => ingredient.name);
        this.printableIngredients = [];
        this.defaultIngredients = this.defaultIngredients.map(defIngredient => ({
          ...defIngredient,
          isSelected: this.orderedItemService
            .getAllIngredients(this.orderedItem)
            .some(ingredient => ingredient.ingredient === defIngredient.name),
        }));
        this.printableIngredients.push(...this.defaultIngredients);
        this.printableIngredients.push(
          ...this.orderedItemService
            .getAllIngredients(this.orderedItem)
            .filter(ingredient => !ingredient.isDefault)
            .map(({ ingredient, isDefault, qualifiers, isLeftHalf, isRightHalf, isSecondHalf, secondHalfQualifiers, isEverything }) => ({
              name: ingredient,
              isDefault,
              isSelected: true,
              qualifiers,
              isLeftHalf,
              isRightHalf,
              isSecondHalf,
              secondHalfQualifiers,
              isEverything,
            }))
        );

        this.printableIngredients = this.printableIngredients.filter(ingredient => visibleIngredients.indexOf(ingredient.name) !== -1);
      }
    }
  }

  onQuantityChanged(quantity: number) {
    this.auditService.createAudit(() => `Quantity changed to ${quantity} for item "${this.orderableItem.name}"`);
    this.orderedItem.quantity = quantity;
    this.determinePrice();
    this.shouldNotFetchPrice = false;
  }

  checkItemValidity() {
    if (this.orderedItem && this.orderableItem) {
      this.isItemValid = this.orderedItemService.isValid(this.orderedItem, this.orderableItem);
    }
    this.cdRef.detectChanges();
  }

  upsellItemAdded(result: boolean) {
    this.hideUpsellItems = result;
  }

  onNextChoiceClicked(choiceInit = false) {
    if (choiceInit) {
      this.scrollToEditItemContent();
    }
  }

  determinePrice() {
    this.orderedItem = this.orderedItem.copy();
    if (!this.orderedItem.quantity || this.orderedItem.quantity < this.orderableItem.minimumQuantity) {
      return;
    }
    this.cdRef.detectChanges();
    this.orderedItemService.getPriceWithIngredients(this.orderedItem).subscribe(
      itemWithIngredientPrices => {
        this.price = itemWithIngredientPrices.itemPrice;
        this.calculateIngredientPrices(itemWithIngredientPrices);
      },
      response => this.resolveOrderErrors(response)
    );
    this.setOrderedDescription();
  }

  removeUnavailableChoices() {
    const dependants = this.orderableItem.choices.map(orderableChoice => ({
      name: orderableChoice.name,
      dependsOn: orderableChoice.dependsOn,
      availability: orderableChoice.dependsOn
        ? !!this.orderedItem.choices.find(c => !!c.ingredients.find(i => i.ingredient === orderableChoice.dependsOn))
        : undefined,
    }));
    this.orderedItem.choices = this.orderedItem.choices.filter(
      orderedChoice =>
        !!dependants.find(dep => dep.name === orderedChoice.name && dep.dependsOn && dep.availability) ||
        !!dependants.find(dep => dep.name === orderedChoice.name && !dep.dependsOn)
    );
  }

  sizeChanges() {
    if (this.storeConfig.showSizeSpecificIngredients) {
      this.calcSizeSpecificIngredients();
    }
    this.calcDefaultIngredients();
    this.calcPrintableIngredients();
    this.determinePrice();
    this.triggerUpsellsOnSizeChange.emit({ orderableItem: this.orderableItem, orderedItem: this.orderedItem });
  }

  setOrderedDescription() {
    if (!this.orderedItem) {
      return;
    }

    const desc = [`${this.orderedItem.size} ${this.orderedItem.item} ${this.orderedItem.category}`];
    this.orderedItemService.getAllIngredients(this.orderedItem).forEach(i => {
      let ingredientDesc = i.ingredient;
      const chunks = [];
      if (i.isLeftHalf) {
        chunks.push(this.translateService.instant('component.item_editor.editor.left').toUpperCase());
      }
      if (i.isRightHalf) {
        chunks.push(this.translateService.instant('component.item_editor.editor.right').toUpperCase());
      }
      if (i.qualifiers && i.qualifiers.length > 0) {
        chunks.push(...i.qualifiers);
      }
      if (chunks.length) {
        ingredientDesc += ` [${chunks.join(', ')}]`;
      }
      desc.push(ingredientDesc);
    });
    this.orderedDescription = desc.join(', ');
  }

  switchToSimpleEditor() {
    this.canvasNotSupported = true;
  }

  onStageAction(stageAction: StageIngredientAction) {
    this.builderService.updateStagedIngredientByOrderable(stageAction);
    this.cdRef.detectChanges();
    this.builderService.stopBuilderImageLoading();
  }

  addOrUpdateItem() {
    if (!this.isItemValid) {
      return;
    }
    this.onAddOrUpdateItem();
    let action$;
    if (this.orderedItem.itemId) {
      this.orderedItem.noticePeriod = this.selectedSizePrice?.noticePeriod;
      action$ = this.orderedItemService.updateItem(this.orderedItem).pipe(
        tap(item => {
          this.gtmService.pushRemoveFromCartEvent({
            items: [this.cachedOrderedItem],
            triggeringArea: EcommerceTriggeringArea.ITEM_EDITOR,
            triggeringPoint: EcommerceTriggeringPoint.UPDATE_BUTTON,
          });
          this.gtmService.pushAddToCartEvent({
            items: [item],
            triggeringArea: EcommerceTriggeringArea.ITEM_EDITOR,
            triggeringPoint: EcommerceTriggeringPoint.UPDATE_BUTTON,
          });
          if (!this.inItemEditorModal) {
            this.itemEditorService.navigateBackToMenu(this.menuRouteLink, this.previousFragment);
          }
        })
      );
    } else {
      this.orderedItem.noticePeriod = this.selectedSizePrice?.noticePeriod;
      action$ = this.orderedItemService.addItemToOrder(this.orderedItem).pipe(
        tap(item => {
          this.gtmService.pushAddToCartEvent({
            items: [item],
            triggeringArea: EcommerceTriggeringArea.ITEM_EDITOR,
            triggeringPoint: EcommerceTriggeringPoint.ORDER_BUTTON,
          });
          this.itemEditorService.navigateBackToMenu(this.menuRouteLink, this.previousFragment);
          setTimeout(() => {
            this.liveAnnouncer.announce(
              this.translateService.instant('component.size_price_button.add_to_order_confrim_message', {
                printName: item.printName,
              }),
              2000
            );
          }, 1000);
        })
      );
    }
    this.loadingSubscription = action$.subscribe(
      () => {
        this.modalService.closeAllModals();
      },
      response => {
        this.resolveOrderErrors(response);
        this.loadingSubscription = null;
        this.cdRef.detectChanges();
      }
    );
  }

  private getDefaultIngredientSelections() {
    if (this.orderableItem) {
      this.orderableItem.choices.forEach(choice => {
        this.defaultIngredientSelections.push(
          ...choice.ingredients
            .filter(ingredient => ingredient.isDefault && ingredient.sizePrices.find(sp => sp.size === this.orderedItem?.size))
            .map(ing => ({
              choice: choice,
              ingredient: ing,
            }))
        );
      });
    }
  }

  getDefaultIngredientSelectionsForHalf(orderableItem: OrderableItem) {
    const defaultIngredientSelections = [];
    if (orderableItem) {
      orderableItem.choices.forEach(choice => {
        defaultIngredientSelections.push(
          ...choice.ingredients
            .filter(ingredient => ingredient.isDefault && ingredient.sizePrices.find(sp => sp.size === this.orderedItem?.size))
            .map(ing => ({
              choice: choice,
              ingredient: ing,
            }))
        );
      });
    }

    return defaultIngredientSelections;
  }

  private calcAddedSelections(selections: IngredientSelection[]) {
    let indexToRemove: number;
    let isDefault: boolean;
    this.defaultIngredientSelections.forEach(selection => {
      if (
        !!selections &&
        selection.choice.name === selections[0].choice.name &&
        selection.ingredient.name === selections[0].ingredient.name
      ) {
        isDefault = true;
      }
    });
    if (!isDefault || (!!selections && !!selections[1])) {
      this.ingredientSelectionsAddedToItem.forEach((selection, index) => {
        if (
          !!selections &&
          selection.choice.name === selections[0].choice.name &&
          selection.ingredient.name === selections[0].ingredient.name
        ) {
          indexToRemove = index;
        }
        if (
          !!selections &&
          !!selections[1] &&
          selection.choice.name === selections[1].choice.name &&
          selection.ingredient.name === selections[1].ingredient.name
        ) {
          this.ingredientSelectionsAddedToItem.splice(index, 1);
        }
      });
      if (indexToRemove >= 0) {
        this.ingredientSelectionsAddedToItem.splice(indexToRemove, 1);
      } else if (!isDefault && !!selections) {
        this.ingredientSelectionsAddedToItem.push(selections[0]);
      }
    }
  }

  private calcSizeSpecificIngredients() {
    const currentNonDefaultIngredients = this.orderedItemService.getNonDefaultIngredients(this.orderedItem).map(ing => ing.ingredient);
    const ingredientsToAddBackToChoice = this.ingredientSelectionsAddedToItem.filter(
      selection => !currentNonDefaultIngredients.find(ing => ing === selection.ingredient.name)
    );
    if (ingredientsToAddBackToChoice.length) {
      let ingToBeShownOnModal = '';
      ingToBeShownOnModal = ingredientsToAddBackToChoice.map(selection => selection.ingredient.name).join(', ');
      const context = new ConfirmationModalContext();
      context.title = this.translateService.instant('component.item_editor.editor.unavailable_size_ingredients_modal.title');
      context.mainContent = this.translateService.instant('component.item_editor.editor.unavailable_size_ingredients_modal.body', {
        ingredients: ingToBeShownOnModal,
        article: ingredientsToAddBackToChoice.length > 1 ? 'are' : 'is',
        selectedSize: this.orderedItem.size,
        previousSize: this.previousSelectedSize,
      });
      context.submitBtnTitle = this.translateService.instant(
        'component.item_editor.editor.unavailable_size_ingredients_modal.submit_button_title',
        {
          previousSize: this.previousSelectedSize,
        }
      );
      context.dismissBtnTitle = this.translateService.instant(
        'component.item_editor.editor.unavailable_size_ingredients_modal.dismiss_btn_title'
      );
      context.type = NotificationModalType.warning;
      context.needDismissAudit = true;
      context.submitObservable = of(true);
      this.auditService.createAudit(() => `${context.title} modal opened`);
      this.modalService.openConfirmationModal(context).subscribe(result => {
        if (result) {
          this.orderedItem.size = this.previousSelectedSize;
          ingredientsToAddBackToChoice.forEach(selection => {
            this.orderedItem.choices
              .find(ch => ch.name === selection.choice.name)
              .ingredients.push(this.orderedIngredientService.createOrderedIngredient(selection.ingredient));
          });
          this.ingredientSelectionsAddedToItem = [];
          this.itemEditorService.setSizeChanged(this.previousSelectedSize);
          this.auditService.createAudit(() => `"${context.submitBtnTitle}" button has been clicked`);
          this.auditService.createAudit(() => `Size changed to ${this.previousSelectedSize} for item "${this.orderableItem.name}"`);
        } else {
          if (result === null) {
            this.auditService.createAudit(() => `"${context.dismissBtnTitle}" button has been clicked`);
            this.auditService.createAudit(
              () =>
                `"${ingToBeShownOnModal}" ${ingredientsToAddBackToChoice.length > 1 ? 'ingredients' : 'ingredient'} ${
                  ingredientsToAddBackToChoice.length > 1 ? 'have' : 'has'
                } been removed from item "${this.orderableItem.name}"`
            );
          }
          this.previousSelectedSize = this.orderedItem.size;
          this.ingredientSelectionsAddedToItem = [];
          const nonDefaultIngredients = Utils.flatten(
            this.orderedItem.choices.map(ch => {
              return ch.ingredients
                .filter(ing => !ing.isDefault)
                .map(ing => ({
                  choice: ch.name,
                  ingredient: ing.ingredient,
                }));
            })
          );

          this.orderableItem.choices.forEach(choice => {
            choice.ingredients.forEach(ing => {
              const ingredientExist = nonDefaultIngredients.find(value => value.ingredient === ing.name && choice.name === value.choice);
              if (ingredientExist) {
                this.ingredientSelectionsAddedToItem.push({
                  choice: choice,
                  ingredient: ing,
                });
              }
            });
          });
        }
      });
    } else {
      this.previousSelectedSize = this.orderedItem.size;
    }
  }

  private identifyVisibleChoices() {
    if (!(this.orderableItem && this.orderedItem)) {
      return [];
    }
    return this.orderedItemService.getFilteredIngredientChoices(this.orderableItem, this.orderedItem);
  }

  private scrollToEditItemContent() {
    if (!this.editItemContent) {
      return;
    }
  }

  private calcIngredientChoicesType() {
    if (!!this.orderableItem && this.orderableItem.ingredientChoicesType) {
      this.ingredientChoicesAsSteps = this.orderableItem.ingredientChoicesAsSteps;
    } else if (this.theme) {
      this.ingredientChoicesAsSteps = this.theme.ingredientChoicesAsSteps;
    }
  }

  private resolveOrderErrors(err) {
    this.errors = this.errorsService.parseAllErrorsFromResponse(err);
    let content = '';
    this.errors.allErrors.map(error => (content += `<p>${this.parseNestedError(error)}</p>`));
    this.modalService.openErrorNotificationModal(this.translateService.instant('component.item_editor.editor.error_order'), content);
  }

  private parseNestedError(err: any): string {
    if (!err) {
      return this.translateService.instant('component.item_editor.editor.network_error');
    }
    return typeof err === 'string' ? err : this.parseNestedError(this.getFirstValueOfObject(err));
  }

  private getFirstValueOfObject(obj: Object): any {
    for (const key in obj) {
      if (obj.hasOwnProperty.call(obj, key)) {
        return obj[key];
      }
    }
  }

  private calculateIngredientPrices(itemWithIngredientPrices: ItemWithIngredientPrices) {
    // We only need to create new orderedItem reference and update prices for if the visible choice has prices enabled
    if (this.choicesWithIngPrices.length > 0 && this.choicesWithIngPrices.indexOf(this.visibleChoice?.name) !== -1) {
      const orderedItemCopy = this.orderedItem.copy();
      orderedItemCopy.choices.map(choice => {
        if (!!itemWithIngredientPrices.ingredientPrices[choice.name]) {
          choice.ingredients.map(ingr => (ingr.price = itemWithIngredientPrices.ingredientPrices[choice.name][ingr.ingredient]));
        }
      });
      this.orderedItem = orderedItemCopy;
    }
    this.cdRef.detectChanges();
  }

  fetchItems() {
    if (!this.orderedItem || !this.isHalfAndHalf) return;

    this.loadingSpecialtyHalfs = true;
    this.orderingUtilsService
      .getHalfs(this.orderedItem.category, this.orderedItem.size)
      .pipe(
        switchMap(response => {
          if (this.rightSelectedHalf) {
            if (!response.find(half => half.name === this.rightSelectedHalf.name)) {
              this.clearHalf(HalfSelectionSideType.RIGHT);
            }
            this.halfsOrderingService.cleanRightHalf(this.orderableItem, this.orderedItem.size);
          }
          if (this.leftSelectedHalf) {
            if (!response.find(half => half.name === this.leftSelectedHalf.name)) {
              this.clearHalf(HalfSelectionSideType.LEFT);
            }
            this.halfsOrderingService.cleanLeftHalf(this.orderableItem, this.orderedItem.size);
          }

          this.halfsOrderingService.calculateItemWithHalfs(
            this.orderedItem,
            this.orderableItem,
            this.halfsOrderingService.leftHalfOrderedItem.getValue(),
            this.halfsOrderingService.rightHalfOrderedItem.getValue()
          );
          this.halfs = response;

          this.cdRef.detectChanges();
          return response && response.length > 0
            ? of(null)
            : this.modalService
                .openErrorNotificationModal(this.translateService.instant('component.item_editor.editor.specialty_halfs_unavailable'))
                .pipe(tap(() => this.reloaderService.reload()));
        }),
        catchError(error => {
          const err = this.errorsService.parseAllErrorsFromResponse(error);
          const message = err?.message
            ? err.message
            : this.translateService.instant('component.item_editor.editor.specialty_halfs_unavailable');
          return this.modalService.openErrorNotificationModal(message).pipe(tap(() => this.reloaderService.reload()));
        })
      )
      .subscribe(() => {
        this.changedSizeInHalf = false;
        this.loadingSpecialtyHalfs = false;

        this.setHeadersTopToElement();
        this.onIngredientChanged();
        this.cdRef.detectChanges();
      });
  }

  toggleSelectHalf(half: SpecialtyHalfs) {
    if (this.activeHalfSide === HalfSelectionSideType.LEFT) {
      this.leftSelectedHalf = half;
      this.leftPrintableIngredients = [];
      this.auditService.createAudit(() => {
        return `Selected "${this.leftSelectedHalf.name}" for left half`;
      });
    }
    if (this.activeHalfSide === HalfSelectionSideType.RIGHT) {
      this.rightSelectedHalf = half;
      this.rightPrintableIngredients = [];
      this.auditService.createAudit(() => {
        return `Selected "${this.rightSelectedHalf.name}" for right half`;
      });
    }

    this.hypermedia
      .get(half.links, 'categoryItem')
      .pipe(map((json: Object): OrderableItem => new OrderableItem().deserialize(json)))
      .subscribe(value => {
        this.isProcessingIngredientChange = true;
        if (this.activeHalfSide === HalfSelectionSideType.LEFT) {
          this.halfsOrderingService.leftHalfOrderedItem.next(this.orderedItemService.createOrderedItem(value, this.orderedItem?.size));
          this.halfsOrderingService.setDefaultLeftHalfs();
          this.orderedItem.specialtyLeftHalf = this.halfsOrderingService.leftHalfOrderedItem.getValue().item;
          this.determinePrice();
        } else {
          this.halfsOrderingService.rightHalfOrderedItem.next(this.orderedItemService.createOrderedItem(value, this.orderedItem?.size));
          this.halfsOrderingService.setDefaultRightHalfs();
        }
        this.halfsOrderingService.calculateItemWithHalfs(
          this.orderedItem,
          this.orderableItem,
          this.halfsOrderingService.leftHalfOrderedItem.getValue(),
          this.halfsOrderingService.rightHalfOrderedItem.getValue()
        );

        this.outletRef?.clear();
        this.outletRef?.createEmbeddedView(this.contentRef);

        if (this.activeHalfSide === HalfSelectionSideType.LEFT) {
          if (this.isItemValid) {
            this.changeHalf(HalfSelectionSideType.RIGHT);
          } else {
            this.hiddenChoices = false;
          }
        } else {
          if (!this.isItemValid) {
            this.hiddenChoices = false;
          }
        }
        this.isProcessingIngredientChange = false;
      });
    this.cdRef.detectChanges();
  }

  changeHalf(side: HalfSelectionSideType, force?: boolean) {
    if (!this.isItemValid && !force) {
      this.hiddenChoices = false;
    } else {
      this.hiddenChoices = true;
    }
    this.activeHalfSide = side;
    this.halfsOrderingService.activeHalfSide.next(side);
    this.scrollableElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    this.outletRef.clear();
    this.outletRef.createEmbeddedView(this.contentRef);
    this.cdRef.detectChanges();
    this.setHeadersTopToElement();

    this.auditService.createAudit(() => {
      return `${side} half selection`;
    });
  }

  clearHalf(side: HalfSelectionSideType) {
    if (side === HalfSelectionSideType.LEFT) {
      this.halfsOrderingService.leftHalfOrderedItem.next(null);
      this.leftSelectedHalf = null;
    } else {
      this.halfsOrderingService.rightHalfOrderedItem.next(null);
      this.rightSelectedHalf = null;
    }
    this.hiddenChoices = true;
    this.halfsOrderingService.activeHalfSide.next(side);
    this.onIngredientChanged();
    this.cdRef.detectChanges();
  }

  modifyHalf(side: HalfSelectionSideType) {
    this.halfsOrderingService.activeHalfSide.next(side);
    this.hiddenChoices = false;
    this.outletRef.clear();
    this.outletRef.createEmbeddedView(this.contentRef);
    this.refreshUpsells();
    this.cdRef.detectChanges();

    this.auditService.createAudit(() => {
      return `Selected ${side} half customize button`;
    });
  }

  private refreshUpsells() {
    this.orderUpsellService.getUpsells(this.orderableItem, this.orderedItem, true).subscribe(upsellWrappers => {
      this.upsellItems = upsellWrappers.flatMap(wrapper => wrapper.items);
      this.cdRef.detectChanges();
    });
  }

  toggleSpecialtyHalfsDetails() {
    this.showSpecialtyHalfDetails = !this.showSpecialtyHalfDetails;
  }

  changeSize(size: string) {
    this.selectedSizePrice = this.orderableItem.getSizePrice(size);
    this.formattedNoticePeriod = this.orderingUtilsService.formatNoticePeriod(this.selectedSizePrice?.noticePeriod);
    this.changedSizeInHalf = true;
    this.fetchItems();
  }

  private setHeadersTopToElement(): void {
    this.viewPort$.subscribe(viewport => {
      let top = 0;
      const overflowOfParent = window.getComputedStyle(document.getElementsByTagName('main')[0]).overflowX;
      if (overflowOfParent == 'hidden' && !viewport.isMobile && !this.theme.showItemEditorModal) {
        this.headerHeightOffset = 0;
        return;
      }
      if (viewport.isMobile) {
        const header = document.getElementById('editor-header');
        if (!!header) top = header.getBoundingClientRect().height - 1;
      } else if (this.theme.showItemEditorModal) {
        top = -20;
      } else {
        const header = document.getElementsByTagName('header')[0];
        let expandedHeaderOffset = 0;
        if (!this.theme.compactHeader && !this.theme.minifiedHeader) {
          const headerBanner = document.getElementById('store-logo-container');
          if (!!headerBanner) expandedHeaderOffset = headerBanner.getBoundingClientRect().height;
        }
        if (!!header) top = header.getBoundingClientRect().height - expandedHeaderOffset;
      }
      this.headerHeightOffset = top;
    });
  }
}
